import { Component, HostListener, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, NgModel, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from 'angular2-toaster';
import { PendingChangesAware } from 'app/components/guards/pendingChanges.guard';
import { DeleteModalComponent } from 'app/components/modal/deleteModal.component';
import { Globals } from 'app/globals/globals';
import { ModalService } from 'app/services/modal.service';
import { PresentationService } from 'app/services/presentation/presentation.service';
import { Sleep } from 'app/services/sleep';
import { CustomValidators } from 'app/shared/customValidators';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { LayoutResolver } from 'app/views/presentations/layouts/layoutResolver';
import { LayoutSelectorComponent } from 'app/views/presentations/layouts/layoutSelector.component';
import { PresentationModel, Section } from 'app/views/presentations/models/presentation.model';
import { Themes } from 'app/views/presentations/themes';
import { WidgetResolver } from 'app/views/presentations/widgets/widgetResolver';
import { WidgetSelectorComponent } from 'app/views/presentations/widgets/widgetSelector.component';
import { Subscription } from 'rxjs';
import { ErrorWithResourceKey } from '../../shared/errorWithResourceKey';

@Component({
    selector: 'psm-edit-presentation',
    templateUrl: 'editPresentation.component.html',
    styleUrls: ['editPresentation.component.scss']
})

export class EditPresentationComponent implements OnInit, PendingChangesAware {
    public isUploading: boolean = false;
    public isNewPresentation: boolean = false;
    public isSectionSelected: boolean;
    public isLoading: boolean;
    public isSaving: boolean;
    public thumbnailPath: string;
    private logger: Logger;

    public presentation: PresentationModel;
    public readonly themes: string[];

    public sectionsTypes: string[];
    public invalidSections: Set<number>;

    public numberOfSections: number = 0;

    @ViewChild('loadingContainer', { read: ViewContainerRef }) private editorContainer: ViewContainerRef;

    @ViewChild(WidgetSelectorComponent) private widgetSelector: WidgetSelectorComponent;
    @ViewChild(LayoutSelectorComponent) private layoutSelector: LayoutSelectorComponent;
    public currentSectionId: number;
    private sectionChanged: Subscription;

    private form: FormGroup;
    private mainGroup: FormGroup;
    public name: FormControl;
    public theme: FormControl;
    public description: FormControl;
    public userTriedToSave: boolean = false;

    @ViewChild('slideshowEditor') public slideshowEditor;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toasterService: ToasterService,
        private loggerFactory: LoggerFactory,
        private globals: Globals,
        private presentationService: PresentationService,
        private modalService: ModalService,
        private formBuilder: FormBuilder,
        private widgetResolver: WidgetResolver,
        private layoutResolver: LayoutResolver
    ) {
        this.logger = loggerFactory.create('EditPresentationComponent');
        this.themes = Object.values(Themes);
    }

    public updateForm() {
        this.presentation.name = this.name.value;
        this.presentation.theme = this.theme.value;
        this.presentation.description = this.description.value;
    }

    public async ngOnInit() {
        let id = this.route.snapshot.params.id;
        this.isNewPresentation = id === undefined;

        if (this.isNewPresentation) {
            this.presentation = await this.presentationService.createPresentation();
        } else {
            try {
                this.presentation = await this.presentationService.getPresentation(id);
            } catch (err) {
                this.logger.warn(`An error of type ${err.name} occured while getting presentations.`);
                this.toasterService.pop('error', '', 'modules.presentations.edit.error-getting-presentation');
                this.router.navigate(['presentations']);
            }
        }

        this.numberOfSections = this.layoutResolver.resolveComponentMetadata(this.presentation.layout).numberOfSections;
        await this.generateSectionsFormControls();

        this.name = new FormControl(this.presentation.name, [
            CustomValidators.requiredWithCustomMessage('errors.name-required'),
            Validators.minLength(1),
            Validators.maxLength(this.globals.shortStringLength)
        ]);

        this.theme = new FormControl(this.presentation.theme, [
            CustomValidators.isInEnum(Themes, 'modules.presentations.edit.invalid-theme-error')
        ]);

        this.description = new FormControl(this.presentation.description, [
            Validators.maxLength(this.globals.longStringLength)
        ]);

        this.mainGroup = this.formBuilder.group({
            name: this.name,
            description: this.description,
            theme: this.theme
        });

        this.form = this.formBuilder.group({
            main: this.mainGroup
        });

        await this.refreshSectionForm();

        this.form.valueChanges.subscribe(() => this.updateForm());
        this.isLoading = false;
    }

    @HostListener('window:beforeunload')
    public hasPendingChanges() {
        return this.form.dirty;
    }

    private checkIfContains(str: string, substr: string): boolean {
        return (str.toLocaleLowerCase()).indexOf(substr.toLocaleLowerCase()) > -1;
    }

    private async openDeleteModal() {
        return this.modalService.open(DeleteModalComponent, {
            title: 'modules.presentations.edit.delete-confirm'
        });
    }

    public async deletePresentation() {
        try {
            let deleteModalResponse = await this.openDeleteModal();
            if (deleteModalResponse) {
                await this.presentationService.deletePresentation(this.presentation.id);
                this.toasterService.pop('success', '', 'modules.presentations.edit.delete-success');
                this.router.navigate(['presentations']);
            }
        } catch (err) {
            this.logger.error(err.message);
            this.toasterService.pop('error', '', 'errors.delete-error');
        }
    }

    public getControlErrors(control: FormGroup | FormArray): string[] {
        if (!control.invalid) return [];
        let errors = [];
        if (control.errors) {
            // tslint:disable-next-line:forin
            let er = control.errors;
            if (er) {
                for (let key in er) {
                    if (er.hasOwnProperty(key)) {
                        errors.push(key);
                    }
                }
            }
        }
        if (control.controls) {
            // tslint:disable-next-line:forin
            for (let key in control.controls) {
                let innerControl = control.controls[key];
                errors.push(...this.getControlErrors(innerControl));
            }
        }
        return errors;
    }

    public async savePresentation() {
        if (!this.presentationHasBeenModified()) { return; }
        if (!this.validatePresentation()) { return; }

        this.isSaving = true;
        try {
            // We remove the extra sections that were cached
            this.presentation.sections = this.presentation.sections.slice(0, this.numberOfSections);

            if (this.presentation.hasPendingUploads()) {
                this.toasterService.pop('info', '', 'modules.presentations.edit.file-upload-ongoing');
            }

            await this.presentationService.savePresentation(this.presentation);
            this.presentation = await this.presentationService.getPresentation(this.presentation.id);
            this.subscribeCurrentSectionToEditor();
            this.toasterService.pop('success', '', 'modules.presentations.edit.saving-success');

            if (this.isNewPresentation) {
                this.isNewPresentation = false;
                this.router.navigate([`presentations/edit/${this.presentation.id}`]);
            }

            this.form.markAsPristine();
        } catch (err) {
            if (err instanceof SyntaxError) {
                this.toasterService.pop('error', '', 'modules.presentations.edit.invalid-json');
            } else if (err instanceof ErrorWithResourceKey) {
                this.logger.error(`An error of type ${err.name} occured`, err);
                this.toasterService.pop('error', '', err.resourceKey);
            } else {
                this.logger.error(err.message);
                this.toasterService.pop('error', '', 'errors.saving-error');
            }
        } finally {
            this.isSaving = false;
        }
    }

    public validatePresentation(): boolean {
        this.updateInvalidSections();
        let errors = this.getControlErrors(this.form);
        for (const error of errors) {
            this.toasterService.pop('error', '', error);
        }

        if (this.hasEmptySections()) {
            this.toasterService.pop('error', '', 'modules.presentations.edit.some-sections-are-empty');
        }

        return (!this.form.invalid) && this.invalidSections.size === 0 && !this.hasEmptySections();
    }

    public presentationHasBeenModified(): boolean {
        return !this.form.pristine;
    }

    public onLayoutClicked(layout: string) {
        this.form.markAsDirty();
        this.widgetSelector.clearEditor();
        this.isSectionSelected = false;
        this.presentation.layout = layout;
        this.numberOfSections = this.layoutResolver.resolveComponentMetadata(this.presentation.layout).numberOfSections;

        // Call the widget changed func (so we update the form status and update the section form controls)
        this.onWidgetChanged();
    }

    public onSectionChanged(ids: [number, number]) {
        const previousSectionId = ids[0];
        const newSectionId = ids[1];
        this.isSectionSelected = newSectionId !== undefined;

        if (this.presentation.sections[newSectionId] === undefined) {
            this.presentation.sections[newSectionId] = new Section();
        }

        let previousSection = this.presentation.sections[previousSectionId];
        if (previousSection && previousSection.formControls) {
            for (const control of previousSection.formControls) {
                control.markAsTouched();
            }
        }

        this.currentSectionId = newSectionId;
        let section = this.presentation.sections[newSectionId];
        this.subscribeCurrentSectionToEditor();
    }

    private async refreshSectionForm() {
        this.form.removeControl('sections');
        this.form.addControl('sections', await this.getFormControls());
        this.sectionsTypes = this.presentation.sections.map(x => x.type);
    }

    public async onWidgetChanged() {
        this.refreshSectionForm();

        // If the section was invalid, we mark it as valid until the user saves (otherwise everything would be red at first)
        if (this.invalidSections && this.invalidSections.has(this.currentSectionId)) {
            const isValid = this.presentation.sections[this.currentSectionId].formControls.every(x => x.valid);
            if (isValid) {
                this.invalidSections.delete(this.currentSectionId);
            }
        }
    }

    private async subscribeCurrentSectionToEditor() {
        let section = this.presentation.sections[this.currentSectionId];
        if (section === undefined) { return; }

        if (this.sectionChanged !== undefined) {
            this.sectionChanged.unsubscribe();
        }

        this.widgetSelector.clearBackups();
        await this.widgetSelector.changeEditor(section.type, section.content, section.files, true);

        let receivedSectionContentOnce = false;
        this.sectionChanged = this.widgetSelector.getSection().subscribe(s => {
            if (receivedSectionContentOnce) {
                this.form.markAsDirty();
            }

            section.content = s.content;
            section.files = s.files;
            section.type = s.type;
            section.formControls = s.formControls;
            receivedSectionContentOnce = true;
        });
    }

    public isValid(input: NgModel) {
        return !(input.invalid && (input.dirty || input.touched));
    }

    public async getFormControls() {
        let sectionsArray = new FormArray([]);

        for (const section of this.presentation.sections.slice(0, this.numberOfSections)) {
            if (section && section.formControls && section.formControls.length) {
                sectionsArray.push(new FormArray(section.formControls));
            }
        }

        return sectionsArray;
    }

    private hasEmptySections() {
        for (let i = 0; i < this.numberOfSections; ++i) {
            if (!this.presentation.sections[i] || !this.presentation.sections[i].type) { return true; }
        }

        return false;
    }

    private updateInvalidSections() {
        this.invalidSections = new Set<number>();
        for (let i = 0; i < this.numberOfSections; ++i) {
            const section = this.presentation.sections[i];

            const isValid = section && section.type && section.formControls.every(x => x.valid);
            if (!isValid) {
                this.invalidSections.add(i);
            }
        }
    }

    private async generateSectionsFormControls() {
        // HACK: Create the sections temporary to create the form controls, then validate the result.
        //       This should be done another way at some point but works for now
        if (this.presentation.sections) {
            for (const section of this.presentation.sections) {
                if (section && section.type && !section.formControls) {
                    let componentFactory = this.widgetResolver.resolveComponent(section.type);
                    let componentRef = this.editorContainer.createComponent(componentFactory);
                    componentRef.instance.content = section.content;
                    componentRef.instance.files = section.files;
                    await componentRef.instance.initialize();
                    section.formControls = await componentRef.instance.getFormControls();

                    componentRef.destroy();
                }
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/views/presentations/editPresentation.component.ts